// Server codes → the mail bundle (CORE-MAIL-SETUP-STEPS-AND-ERRORS-NEED-I18N-CODES-1).
//
// The API serves the admin setup steps and the operator-configuration errors
// as customer-safe ENGLISH (server content/setupSteps.js, configErrors.js) —
// and, beside each sentence, a stable code plus `params`, the values that
// sentence names. A Georgian workspace read that English on /admin/mail; the
// code is what it translates by now:
//
//   a step    { id, title, body, bodyCode, params?, code? }
//             `id` names the title, `bodyCode` the exact body sentence,
//             `params` fill its holes; `code` is the copyable line (a scope
//             list, a URL, a command) and is never translated.
//   an error  the route answers { error: <code>, message, params? } and the
//             SDK throws it with that body on `err.cause`; a health row
//             carries sync.lastErrorCode + sync.lastErrorParams beside the
//             stored sync.lastError.
//
// The server's English is the fallback, never an error: a code this bundle
// does not know (a newer server), no code at all (an older one), a key that
// does not resolve (polyglot echoes a missing key) and a hole the params leave
// unfilled all paint the English as it arrived.
//
// LITERAL key maps — the i18n two-way gate (i18nMailKeys.test.js) extracts
// string literals, so a key built from a code would read as an unused bundle
// key (the MAIL_INVITE_STATUS_KEYS idiom in integrations.js).

const MAIL_SETUP_STEP_TITLE_KEYS = {
  google_connect: 'mail.setup.googleConnect.title',
  google_delegation: 'mail.setup.googleDelegation.title',
  google_test: 'mail.setup.googleTest.title',
  microsoft_consent: 'mail.setup.microsoftConsent.title',
  microsoft_policy: 'mail.setup.microsoftPolicy.title',
  microsoft_test: 'mail.setup.microsoftTest.title'
}

const MAIL_SETUP_STEP_BODY_KEYS = {
  google_connect_instructions: 'mail.setup.googleConnect.instructions',
  google_connect_connected: 'mail.setup.googleConnect.connected',
  google_connect_connected_domain: 'mail.setup.googleConnect.connectedDomain',
  google_delegation_instructions: 'mail.setup.googleDelegation.instructions',
  google_delegation_scopes_missing: 'mail.setup.googleDelegation.scopesMissing',
  google_test_instructions: 'mail.setup.googleTest.instructions',
  microsoft_consent_instructions: 'mail.setup.microsoftConsent.instructions',
  microsoft_consent_app_missing: 'mail.setup.microsoftConsent.appMissing',
  microsoft_consent_connected: 'mail.setup.microsoftConsent.connected',
  microsoft_policy_instructions: 'mail.setup.microsoftPolicy.instructions',
  microsoft_policy_app_missing: 'mail.setup.microsoftPolicy.appMissing',
  microsoft_test_instructions: 'mail.setup.microsoftTest.instructions'
}

const MAIL_CONFIG_ERROR_KEYS = {
  provider_not_configured: 'mail.error.providerNotConfigured',
  token_vault_not_configured: 'mail.error.tokenVaultNotConfigured',
  signing_not_configured: 'mail.error.signingNotConfigured',
  push_not_configured: 'mail.error.pushNotConfigured'
}

// provider_not_configured names the provider when the server knows it
// (`params.provider`) and says "this mail provider" when it does not — two
// sentences for one code, so the second one has its own key.
const MAIL_CONFIG_ERROR_UNNAMED_KEYS = {
  provider_not_configured: 'mail.error.providerNotConfiguredGeneric'
}

// One bundle sentence with its `{holes}` filled from `params`, or '' when the
// key does not resolve or a hole has no value — the caller then paints the
// server's English, so a raw `{hole}` never reaches the screen.
const _mailServerCopy = (el, key, params) => {
  if (!key || !el || typeof el.call !== 'function') return ''
  const raw = el.call('polyglot', key)
  if (typeof raw !== 'string' || !raw || raw === key) return ''
  let unfilled = false
  const text = raw.replace(/\{(\w+)\}/g, (hole, name) => {
    const value = params && params[name]
    if (value == null || value === '') {
      unfilled = true
      return hole
    }
    return String(value)
  })
  return unfilled ? '' : text
}

// A step's title. `label` is the setup gate's provider prefix
// ("Google Workspace · …"); the /admin/mail card names the provider in its
// own head and passes none.
export const mailSetupStepTitle = function (step) {
  const s = step || {}
  const title = _mailServerCopy(this, MAIL_SETUP_STEP_TITLE_KEYS[s.id]) || s.title || ''
  return s.label ? `${s.label} · ${title}` : title
}

// A step's body — the sentence its `bodyCode` names, filled from `params`.
export const mailSetupStepBody = function (step) {
  const s = step || {}
  return _mailServerCopy(this, MAIL_SETUP_STEP_BODY_KEYS[s.bodyCode], s.params) || s.body || ''
}

// The plain parts of one mail error — `{ code, params, message }` — from an
// SDK error (the route answer rides on `err.cause`) or from parts the shell
// already holds (a notice, a health row). Plain data: a writer keeps it in
// state and the render site translates it, so a language switch repaints it.
export const mailErrorOf = function (source) {
  const src = source || {}
  const body = src.cause && typeof src.cause === 'object' ? src.cause : null
  const code =
    (body && typeof body.error === 'string' && body.error) ||
    (typeof src.code === 'string' && src.code) ||
    ''
  const params = (body && body.params) || src.params
  return {
    code,
    params: params && typeof params === 'object' ? params : null,
    message: typeof src.message === 'string' ? src.message : ''
  }
}

// One mail error in the session language: the bundle's sentence for a config
// code it knows, the server's own sentence for everything else.
export const mailErrorText = function (source) {
  const { code, params, message } = mailErrorOf(source)
  const named = !!(params && params.provider)
  const key = (!named && MAIL_CONFIG_ERROR_UNNAMED_KEYS[code]) || MAIL_CONFIG_ERROR_KEYS[code]
  return _mailServerCopy(this, key, params) || message
}

// The /admin/mail health row's failure line (serializeAdminAccount): the
// stored sync failure by its code, else the connect-time `lastAuthError`,
// which holds a bare code — a config code translates, anything else shows as
// stored.
export const mailAdminRowError = function (row) {
  const r = row || {}
  const sync = r.sync || {}
  if (sync.lastError) {
    return mailErrorText.call(this, {
      code: sync.lastErrorCode,
      params: sync.lastErrorParams,
      message: sync.lastError
    })
  }
  if (r.lastAuthError) {
    return mailErrorText.call(this, { code: r.lastAuthError, message: r.lastAuthError })
  }
  return ''
}
